<template>
  <div
    class="fixed top-0 left-0 right-0 z-[55] flex flex-col items-center justify-center h-screen overflow-hidden bg-black/50 p-6"
    @click.self="$emit('close')">
    <div
      class="relative w-full max-w-[95vw] bg-white shadow-md rounded-lg p-6 max-h-[65vh] flex flex-col">
      <!-- Close Button -->
      <button
        type="button"
        @click="$emit('close')"
        class="absolute right-2.5 top-3 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-700 hover:bg-gray-200 hover:text-gray-900">
        <IconXmark class="w-6 h-6" />
      </button>

      <!-- Title -->
      <h3 class="py-2 text-xl leading-3 mb-4">
        {{ $t('document-type.previewColumns') }}
      </h3>

      <!-- Content -->
      <div class="overflow-auto flex-1">
        <div class="mb-4 text-sm text-gray-600">
          {{ $t('document-type.previewDescription') }}
        </div>

        <!-- Preview Table -->
        <div class="border rounded-lg overflow-hidden bg-white shadow-sm">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    v-for="column in previewColumns"
                    :key="column.fieldName"
                    :style="{ width: column.width ? `${column.width}px` : 'auto' }"
                    class="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 last:border-r-0">
                    {{ column.caption }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <!-- Empty rows to show structure -->
                <tr v-for="i in 6" :key="i" class="h-10 bg-gray-50/30">
                  <td
                    v-for="column in previewColumns"
                    :key="column.fieldName"
                    class="px-3 py-2 text-sm text-gray-400 border-r border-gray-100 last:border-r-0">
                    <div class="h-4 bg-gray-200/50 rounded animate-pulse"></div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Legend -->
        <div class="mt-4 p-3 bg-theme-50 rounded-lg">
          <div class="flex items-start gap-2">
            <IconInfo class="size-5 text-theme-600 flex-shrink-0 mt-0.5" />
            <div class="text-sm text-theme-800">
              <p class="font-medium mb-1">{{ $t('document-type.previewNote') }}</p>
              <ul class="list-disc list-inside space-y-1 text-xs">
                <li>{{ $t('document-type.previewNoteColumns') }}</li>
                <li>{{ $t('document-type.previewNoteWidth') }}</li>
                <li>{{ $t('document-type.previewNoteOrder') }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end w-full gap-2 mt-4">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          @click="$emit('close')">
          {{ $t('common.actions.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import IconInfo from '@/assets/icons/IconInfo.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import type { ColumnDefinitionType } from '@/types/DocumentTypes';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  columnDefinitionsJson: string;
}>();

defineEmits(['close']);

interface PreviewColumn {
  fieldName: string;
  caption: string;
  width: number;
  order: number;
}

const previewColumns = computed<PreviewColumn[]>(() => {
  if (!props.columnDefinitionsJson) return [];

  try {
    const parsed = JSON.parse(props.columnDefinitionsJson) as ColumnDefinitionType;
    const columns: PreviewColumn[] = [];

    // Process DocumentDetails to extract all columns
    parsed.DocumentDetails?.forEach((detail) => {
      // Add common fields
      detail.CommonFields?.forEach((field) => {
        columns.push({
          fieldName: field.FieldName,
          caption: field.Caption || field.FieldName,
          width: field.Width || 100,
          order: field.OrderNumber || 0,
        });
      });

      // Add specific fields
      detail.SpecificFields?.forEach((field) => {
        columns.push({
          fieldName: field.FieldName,
          caption: field.Caption || field.FieldName,
          width: field.Width || 100,
          order: field.OrderNumber || 0,
        });
      });
    });

    // Sort by order number, putting order 0 at the end sorted by caption
    return columns.sort((a, b) => {
      // If both have order 0, sort by caption
      if (a.order === 0 && b.order === 0) {
        return a.caption.localeCompare(b.caption);
      }
      // If only a has order 0, put it after b
      if (a.order === 0) return 1;
      // If only b has order 0, put it after a
      if (b.order === 0) return -1;
      // Otherwise sort by order number
      return a.order - b.order;
    });
  } catch (error) {
    toastStore.error({ text: `${$t('messages.errors.columnDefinitionsJsonError')}` });
    console.error('Failed to parse column definitions for preview:', error);
    return [];
  }
});
</script>
